import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { APP_MODAL_ROUTES } from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import { createStyles } from '@/contexts/theme-context';
import { EditNoteContentV2 } from '@/components/order-guide/edit-note-content-v2';
import { ItemsContext_OrderGuideItemFieldsFragment } from '@/types';
import { ModalContainer } from '@/components/modal-container';
import { SCREEN_PADDING } from '@/styles';
import { StackScreenProps } from '@react-navigation/stack';
import { UpdateOrderGuideItemSkuList } from '@/components/order-guide/update-order-guide-item-sku-list';
import { useInterceptNavigationBeforeRemove } from '@/hooks/use-intercept-navigation-before-remove';
import { View } from 'react-native';

type Props = StackScreenProps<AppModalStackParams, APP_MODAL_ROUTES.EditNote>;

export const EditNoteScreen: React.FC<Props> = ({ navigation, route }) => {
  const { collections, getOrderGuideItem, itemMetadata, onSave } = route.params;
  const [styles] = useStyles();

  const renderSingleSku = itemMetadata.skus.length <= 1;

  const [orderGuideItem, setOrderGuideItem] =
    useState<ItemsContext_OrderGuideItemFieldsFragment | null>(() =>
      renderSingleSku ? getOrderGuideItem(itemMetadata) : null,
    );

  const title = i18n.t('item_note');

  const {
    invokeCloseHandler,
    markCloseConfirmedAndGoBack,
    registerCloseHandler,
  } = useInterceptNavigationBeforeRemove(navigation);

  const closeCurrentView = useCallback(() => {
    if (renderSingleSku || orderGuideItem === null) {
      markCloseConfirmedAndGoBack();
      return;
    }
    setOrderGuideItem(null);
  }, [markCloseConfirmedAndGoBack, orderGuideItem, renderSingleSku]);

  useEffect(() => {
    registerCloseHandler(closeCurrentView);
  }, [closeCurrentView, registerCloseHandler]);

  const renderContent = () => {
    if (!renderSingleSku && orderGuideItem === null) {
      return (
        <UpdateOrderGuideItemSkuList
          itemMetadata={itemMetadata}
          onSelect={(option) => {
            const nextOrderGuideItem = getOrderGuideItem(option.metadata!.sku);
            if (nextOrderGuideItem) {
              setOrderGuideItem(nextOrderGuideItem);
            }
          }}
        />
      );
    }

    if (!orderGuideItem) {
      return null;
    }

    return (
      <View style={styles.contentContainer}>
        <EditNoteContentV2
          collections={collections}
          onClose={closeCurrentView}
          onRegisterCloseHandler={registerCloseHandler}
          onSave={async (values) => {
            await onSave(orderGuideItem, values);
          }}
          orderGuideItem={orderGuideItem}
        />
      </View>
    );
  };

  return (
    <ModalContainer
      navigationHeaderProps={{
        centerTitle: true,
        onBackPress: invokeCloseHandler,
        showBackButton: true,
        title,
      }}
      showLargeLayoutCloseButton={false}
    >
      {renderContent()}
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  contentContainer: {
    flex: 1,
    paddingHorizontal: SCREEN_PADDING,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
}));
